import Icon from '~/src/common/components/Icon';
import ProductImageLink from '~/src/common/components/ProductImageLink/ProductImageLink';
import I18n from '~/src/common/services/I18n';
import Tracker from '~/src/common/services/Tracker';
import { formatPrice, getPriceGranularity } from '~/src/common/utils/prices';
import { useProductLink } from '~/src/screens/CatalogContent/ContentScreen/hooks/catalogContentLinks';
import { GenericArticleItem } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/components/types';
import { useRecipeScreenAnalyticsContext } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/context';
import { CatalogSource } from '~/src/screens/CatalogContent/ContentScreen/types';

import * as S from './layout';
import PromoPrice from './PromoPrice';
import SubstituteButton from './SubstituteButton';

interface Props {
  article: GenericArticleItem;
  source: CatalogSource;
  originalArticle?: GenericArticleItem;
  onReplace?: () => void;
}

const ArticleItem = ({ article, source, onReplace, originalArticle }: Props) => {
  const { categoryName, subcategoryName } = useRecipeScreenAnalyticsContext();

  const {
    id,
    image,
    images,
    slug,
    name,
    itemDefinition,
    packSize,
    availableQuantity,
    promo,
    hasSubstituteProducts,
    itemPrice,
  } = article || {};

  const priceGranularity = itemDefinition
    ? getPriceGranularity(itemDefinition, { displayWeight: true, packSize })
    : '-';

  const productLink = useProductLink(slug ?? '', {
    eventSource: 'recipe ingredients list',
    source,
  });

  const isAvailable = availableQuantity > 0;

  const analyticsProperties = {
    'product id': id,
    'product name': name,
    'product event source': 'recipe ingredients list' as const,
    'recipe category': categoryName ?? '',
    'recipe subcategory': subcategoryName,
  };

  const canBeReplaced = hasSubstituteProducts ?? originalArticle?.hasSubstituteProducts;

  return (
    <S.ArticleItemContainer>
      {image || images?.[0] ? (
        <ProductImageLink
          // @ts-expect-error en attente de l'homogénéisation des props images
          images={image ? [image] : images}
          productLink={productLink}
          isAvailable={isAvailable}
          dimensions={{ width: 32, height: 48 }}
          showOutStockTag={false}
          onClick={() => {
            Tracker.sendEvent('click ingredient item', analyticsProperties);
          }}
        />
      ) : null}
      <S.ArticleContent>
        <S.ArticleNameRow>
          <S.ItemLink href={{ pathname: productLink.as, query: productLink.query }}>
            <S.ArticleName $isAvailable={isAvailable}>{name}</S.ArticleName>
          </S.ItemLink>
          {originalArticle ? (
            <S.ReplacedTag aria-label={I18n.t('recipe-screen.product-replaced')}>
              <Icon name="Switch2" size={10} color="PRIMARY" />
            </S.ReplacedTag>
          ) : null}
        </S.ArticleNameRow>
        <p>{priceGranularity}</p>
        {isAvailable ? (
          <div>
            {promo && itemPrice ? (
              <PromoPrice packSize={packSize} price={itemPrice} promo={promo} />
            ) : itemPrice ? (
              formatPrice(itemPrice)
            ) : (
              '-'
            )}
            {canBeReplaced && onReplace ? (
              <SubstituteButton
                title={I18n.t('recipe-screen.substitute-button-label')}
                onClick={() => {
                  Tracker.sendEvent('click open replacement options', analyticsProperties);
                }}
                onReplace={onReplace}
              />
            ) : null}
          </div>
        ) : null}
        {!isAvailable && canBeReplaced && onReplace ? (
          <SubstituteButton
            title={I18n.t('recipe-screen.substitute-button-label')}
            onClick={() => {
              Tracker.sendEvent('click cta similar products', analyticsProperties);
            }}
            onReplace={onReplace}
          />
        ) : null}
        {!isAvailable && !hasSubstituteProducts ? (
          <S.NoSimilarProducts>{I18n.t('recipe-screen.no-similar-products')}</S.NoSimilarProducts>
        ) : null}
      </S.ArticleContent>
    </S.ArticleItemContainer>
  );
};

export default ArticleItem;
